import { useEffect, useContext, useState, useMemo, memo, useCallback, Fragment } from 'react';
import { DragDropContext, Draggable, Droppable, DroppableProps } from 'react-beautiful-dnd';
import { Form } from '@/components/ui/form';
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { FetchContext } from '@src/scenario-editor/providers/fetch.context.tsx';
import { type FetchAllMessagesPayload } from '@src/components/node-info/hooks/useFetchMessages.hook';
import { type FetchTaskManagementsPayload } from '@src/components/node-info/hooks/useFetchTaskManagements.hook';
import { useBehavioralRulesData } from '@src/components/node-info/providers/behavioral-rules-data.provider';
// LF360-1938: supplies the scenario display names the condition picker groups by.
import { useFetchScenario } from '@src/components/node-info/hooks/useFetchScenarios.hook';
import { seedTaskWindowSwitch, DEFAULT_LAST_N_PERIOD, DEFAULT_MATCH_MODE } from '@src/components/node-info/utils/lastNTaskWindow';
import { clampAnswersWindow, DEFAULT_ANSWERS_WINDOW } from '@src/components/node-info/utils/answersWindow';
import type {
  CustomNodeData,
  NodeToRenderProps,
  NodeToRenderBehaviorRuleData,
  CustomIfConditionBehaviorNodeData,
  RulesCondition,
  ElseCondition,
  CustomMessageNodeData,
  BehaviorRuleStatementCurrent,
  Operator,
  CustomNode,
  CustomEdge,
} from '@src/components/custom-nodes/types';
import { useAtom } from 'jotai';
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { Selectbox } from '@src/components/selectbox/Selectbox';
import { ComboboxBehavior } from '@src/components/combobox/ComboboxBehavior';
// LF360-1938: per-scenario grouping for the condition picker's sidebar.
import { buildOtherScenarioGroups } from '@src/components/node-info/utils/buildOtherScenarioGroups';
import { AndOrToggle, BranchHeader, AddRow } from '@src/components/node-info/behavior-rules';
import { branchBlockClasses, branchHeaderClasses, criteriaRowClasses, revealOnHoverClasses, rowDeleteButtonClasses, rowDeleteHighlightClasses, staticPillClasses, rowPendingSpinnerClasses, rulePillClasses } from '@src/components/node-info/behavior-rules/styles';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import { reactFlowInstanceAtom, branchUpdaterKey, getOpenedInfoModalCounter, wrapperSizeAtom, dropdownOpenStateAtom } from '@src/atoms';
import { deleteStatementBranch, addStatementBranch, recalculateHandlePositions, reorderStatementBranch } from '@src/scenario-editor/render-utils';
import { recalculateNodesYPosForNodes, recalculateNodesXPos, updateNode } from '@src/scenario-editor/utils';
import { getBehaviorRulesSchema } from '@src/scenario-editor/validator/schemas/behavior-rules';
import { NODES_TO_PASTE_CONDITION_AFTER } from '@src/consts';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { validatorInstance } from '@src/scenario-editor/validator/Validator';
import cn from 'classnames';

// LF360-1823 (#118): text-red-500 - сирий Tailwind-колір; --destructive у темі має те саме
// значення в світлій темі (hsl(0 84.2% 60.2%) === #EF4444), тож візуально це no-op, але
// іконка починає адаптуватися до темної теми. size-4 - ті самі 16px, що й решта іконок.
// LF360-1812: adherence (subject-level participant metric) constants. Module-scope
// so the comboboxOptions useMemo (declared before the in-component consts) can use
// ADHERENCE_METRIC_VALUE without a temporal-dead-zone error.
const ADHERENCE_METRIC_VALUE = 'adherence';
const DEFAULT_ADHERENCE_OPERATOR = 'lt';
const DEFAULT_ADHERENCE_THRESHOLD = 50;
const DEFAULT_ADHERENCE_WINDOW_DAYS = 7;
const ADHERENCE_OPERATOR_OPTIONS: { label: string; value: 'eq' | 'gt' | 'lt' | 'gte' | 'lte' }[] = [
  { label: '=', value: 'eq' },
  { label: '>', value: 'gt' },
  { label: '≥', value: 'gte' },
  { label: '<', value: 'lt' },
  { label: '≤', value: 'lte' },
];

// LF360-1828: Participant Profile (isProfileField) constants. The operator set
// shown depends on the selected field's fieldType (short form of the source
// field's inputType - see useFetchProfileFields.hook.ts); value control (dropdown
// vs text vs date/time input) is chosen the same way, next to the render.
// REUSED as-is (names kept from Task 4) by the Onboarding Responses source
// (isOnboardingResponse) below - fieldType is the same shared vocabulary
// (single|multi|input|picker|date|time) and the same criterion column, just
// keyed by onboardingQuestionId instead of profileFieldId. Onboarding differs
// only in where a dropdown fieldType's own options come from - see
// useFetchOnboardingQuestions.hook.ts (options embedded in the fetch payload,
// no message-text-keyed responsesMap indirection needed).
const DEFAULT_PROFILE_FIELD_OPERATOR = 'eq';
// Field types whose value is picked from a dropdown of the field's own options
// (fetched by message text, same mechanism as regular message-response criteria).
const PROFILE_DROPDOWN_FIELD_TYPES = ['single', 'multi', 'picker'];
const PROFILE_FIELD_OPERATOR_OPTIONS: Record<string, { label: string; value: 'eq' | 'ne' | 'gt' | 'lt' | 'contains' }[]> = {
  single: [{ label: 'is', value: 'eq' }, { label: 'is not', value: 'ne' }],
  picker: [{ label: 'is', value: 'eq' }, { label: 'is not', value: 'ne' }],
  multi: [{ label: 'includes', value: 'eq' }, { label: 'does not include', value: 'ne' }],
  input: [{ label: 'is', value: 'eq' }, { label: 'is not', value: 'ne' }, { label: 'contains', value: 'contains' }],
  date: [{ label: 'is', value: 'eq' }, { label: 'before', value: 'lt' }, { label: 'after', value: 'gt' }],
  time: [{ label: 'is', value: 'eq' }, { label: 'before', value: 'lt' }, { label: 'after', value: 'gt' }],
};
const getProfileFieldOperatorOptions = (fieldType?: string): { label: string; value: string }[] =>
  PROFILE_FIELD_OPERATOR_OPTIONS[fieldType ?? ''] ?? [{ label: 'is', value: 'eq' }];

interface MessageNodeInfoProps {
  selectedNodeProps: NodeToRenderProps;
  disabled: boolean;
}

interface CriteriaError {
  rules?: {
    criteria?: {
      question?: { message: string };
      value?: { message: string };
      period?: { message: string };
    }[];
  };
}
type ExtractCriteriaKeys<T> = T extends { rules?: { criteria?: (infer U)[] } } ? keyof U : never;
type CriteriaErrorProps = ExtractCriteriaKeys<CriteriaError>;

const BehaviorRulesNodeInfoComponent = ({ selectedNodeProps, disabled }: MessageNodeInfoProps) => {
  const selectedNodePropsData = selectedNodeProps?.data as NodeToRenderBehaviorRuleData;
  const reactFlowInstance = useContext(ReactflowInstanceContext);
  const [,setBranchUpdaterKey] = useAtom(branchUpdaterKey);
  const wrapperSize = useAtom(wrapperSizeAtom)[0];
  const [responsesMap, setResponsesMap] = useState<Record<string, string[]>>({})
  const [responseMessagesToFetchOptions, setResponseMessagesToFetchOptions] = useState<string[]>([]);
  const [formValues, setFormValues] = useState<CustomIfConditionBehaviorNodeData['conditions']>(selectedNodePropsData.conditions ?? []);


  // LF360-1933 (BRM-03): the rows currently blocked by an in-flight structural
  // edit. This replaced a per-node boolean in a jotai atom that gated a
  // panel-wide overlay; the state is component-local now because nothing
  // outside this panel ever read it, and because a module-global Map keyed by
  // node id needs delete-on-unmount hygiene that a useState simply does not.
  //
  // It is transient UI state and MUST NOT be written into node data. The React
  // Flow instance is the source of truth and everything in a node's `data`
  // serializes into the saved scenario and reaches the backend, so a row key
  // that leaked in there would become a permanent part of every stored
  // scenario. Nothing here is ever passed to onChange/update/insert/setNodes -
  // __tests__/e2e/behavior-rule-geometry.spec.mjs asserts that in a browser
  // rather than trusting this comment.
  // LF360-1933 round 2: which messages have finished loading their response
  // options.
  //
  // Pending used to be a Set of row keys cleared by a 200ms interval once two
  // seconds had passed with no re-render. That proxy was never the thing being
  // waited for, and it failed in both directions: any unrelated re-render -
  // another row edited, the panel's own fetch resolving, a parent update - reset
  // the clock, while an edit with nothing to wait for still blocked its row for
  // a full two seconds. Measured on the dev harness, one condition change left
  // the row disabled for 4.2s, of which 2.1s was the fetch and the rest was
  // waiting for quiet.
  //
  // The signal is now the one the old code's own comments already named: a
  // response control has nothing to offer until the options for its message
  // have arrived. `responseMessagesToFetchOptions` says which messages are
  // wanted and this says which have come back, so pending is DERIVED from data
  // rather than stored - there is nothing left to mark, to clear, or to leak
  // into node data.
  //
  // Settled by REQUESTED message, not by the keys the response comes back with.
  //
  // `fetchOptionsByMessage` answers `{ message, options }`, and nothing obliges
  // the host to echo the message that was asked for: the prop a host implements
  // is typed `() => Promise<FetchOptionsByMessagePayload>` in ScenarioEditor -
  // no parameter at all - so an implementation is not even told which message it
  // is answering unless it looks. The bundled stub does exactly that and always
  // answers `test`, which is what surfaced this while measuring.
  //
  // Keying this off responsesMap would therefore leave a row pending for ever
  // against any host whose reply is keyed differently, with nothing in the types
  // to say so.
  const [settledMessages, setSettledMessages] = useState<Set<string>>(new Set());

  // Track dropdown open state - persists across re-renders
  const [dropdownOpenState, setDropdownOpenState] = useAtom(dropdownOpenStateAtom);

  // Use the context provider data instead of direct hook calls
  const { allMessages, taskManagements, profileFields, onboardingQuestions, isLoadingMessages: isFetching, isLoadingTaskManagements: isFetchingTaskManagements } = useBehavioralRulesData();

  // LF360-1938: `allMessages` carries a scenarioId per question but no scenario
  // name, so the picker's per-scenario grouping needs this second source to
  // label the groups. The hook's tuple is loosely typed (ScenarioItem[] | boolean),
  // hence the Array.isArray narrowing below - same handling as ComboboxScenarioPicker.
  const [allScenario] = useFetchScenario();

  const scenarioNames = useMemo(
    () => new Map(
      Array.isArray(allScenario)
        ? (allScenario as { id: string; name: string }[]).map((scenario) => [scenario.id, scenario.name] as const)
        : [],
    ),
    [allScenario],
  );

  const isTask = (criteria: { isTaskManagement?: boolean, question?: string }): boolean => {
    // Check both the flag and if the question ID matches a task ID
    const hasFlag = Boolean(criteria.isTaskManagement);
    const isTaskById = criteria.question ? taskManagements.some(task => task.id === criteria.question) : false;
    return hasFlag || isTaskById;
  };

  // Helper functions for managing dropdown open state
  const getDropdownKey = (statementIndex: number, criteriaIndex: number, type: 'condition' | 'response' | 'operator'): string => {
    return `${selectedNodeProps.id}-${statementIndex}-${criteriaIndex}-${type}`;
  };

  const isDropdownOpen = (statementIndex: number, criteriaIndex: number, type: 'condition' | 'response' | 'operator'): boolean => {
    const key = getDropdownKey(statementIndex, criteriaIndex, type);
    return dropdownOpenState.get(key) ?? false;
  };

  const setDropdownOpen = useCallback((statementIndex: number, criteriaIndex: number, type: 'condition' | 'response' | 'operator', open: boolean) => {
    const key = getDropdownKey(statementIndex, criteriaIndex, type);
    setDropdownOpenState(prev => {
      const newMap = new Map(prev);
      if (open) {
        newMap.set(key, true);
      } else {
        newMap.delete(key);
      }
      return newMap;
    });
  }, [selectedNodeProps.id, setDropdownOpenState]);

  // LF360-1933 (BRM-03): per-row pending state. The key is getDropdownKey's
  // above minus its trailing type segment - pending is a property of the whole
  // rule row, not of one control inside it. It is a useCallback where its
  // sibling is a plain function only so the writers below can name it in a
  // dependency array without earning a react-hooks/exhaustive-deps warning.
  const getRowKey = useCallback((statementIndex: number, criteriaIndex: number): string => {
    return `${selectedNodeProps.id}-${statementIndex}-${criteriaIndex}`;
  }, [selectedNodeProps.id]);

  // LF360-1933 round 2: a row is pending exactly while the options its response
  // control needs are still on their way. No marking, no clearing, no timer -
  // the only two facts it reads are what was asked for and what has come back.
  //
  // Read through formValues rather than taking the criterion as an argument, so
  // that all eleven call sites keep their `(statementIndex, criteriaIndex)`
  // shape. The alternative was threading a message through every edit handler,
  // which is how the old marking spread to the eight sites that had nothing to
  // wait for and blocked their row anyway.
  const rowWaitsForMessage = (statementIndex: number, criteriaIndex: number): string | null => {
    const condition = formValues[statementIndex];
    if (!condition || !('rules' in condition)) return null;
    const question = condition.rules.criteria[criteriaIndex]?.question;
    if (!question) return null;
    return responseMessagesToFetchOptions.includes(question) && !settledMessages.has(question)
      ? question
      : null;
  };

  const isRowPending = (statementIndex: number, criteriaIndex: number): boolean =>
    rowWaitsForMessage(statementIndex, criteriaIndex) !== null;

  // LF360-1933 review M-2: one spinner for the node, not one per waiting row.
  // Opening a saved node asks for every unresolved message at once, and showing
  // that as N spinners is what put six of them in a three-branch panel. The
  // first waiting row reports for all of them - computed here rather than held
  // in state, so it cannot disagree with what isRowPending says.
  const firstPendingRowKey = (() => {
    for (let statementIndex = 0; statementIndex < formValues.length; statementIndex += 1) {
      const condition = formValues[statementIndex];
      if (!condition || !('rules' in condition)) continue;
      for (let criteriaIndex = 0; criteriaIndex < condition.rules.criteria.length; criteriaIndex += 1) {
        if (isRowPending(statementIndex, criteriaIndex)) {
          return getRowKey(statementIndex, criteriaIndex);
        }
      }
    }
    return null;
  })();

  const isRowShowingIndicator = (statementIndex: number, criteriaIndex: number): boolean =>
    isRowPending(statementIndex, criteriaIndex)
      && firstPendingRowKey === getRowKey(statementIndex, criteriaIndex);

  const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
  const nodes = instance.getNodes() as CustomNode[];
  const edges = instance.getEdges() as CustomEdge[];

  const fetchContext = useContext(FetchContext)!;
  const shouldShowErrorWarnings = getOpenedInfoModalCounter(selectedNodeProps.id) > 1;

  if (!fetchContext) {
    throw new Error('FetchContext is not provided');
  }

  const messageNodesWithAnswers = nodes
    .filter((node) => (NODES_TO_PASTE_CONDITION_AFTER.includes((node.data as CustomNodeData)?.nodeType ?? '')))

  const messageValues = (messageNodesWithAnswers ?? [])
    .map(item => (item.data as CustomMessageNodeData).message!)
    .filter(Boolean);

  const allMessageValues = (allMessages as { message: string }[])
    .map(item => item.message)
    .filter(Boolean);

  const taskManagementValues = (taskManagements as { title: string; id: string }[])
    .map(item => item.id)
    .filter(Boolean);

  // Combine all values and ensure there's at least one value for EnumType
  const possibleQuestions = [...messageValues, ...allMessageValues, ...taskManagementValues];
  const possibleQuestionsForSchema = possibleQuestions.length
    ? possibleQuestions as [string, ...string[]]
    : ([] as unknown as [string, ...string[]]);

  // Create stable dependency for formValues to avoid infinite loops
  const formValuesKey = useMemo(() => {
    return JSON.stringify(formValues.map(f => 'rules' in f ? f.rules.criteria.map(c => c.question) : []));
  }, [formValues]);

  // Memoize options to prevent unnecessary re-renders in ComboboxBehavior
  const comboboxOptions = useMemo(() => {
    console.log('🟣 comboboxOptions recalculated', {
      messageNodesCount: messageNodesWithAnswers.length,
      allMessagesCount: allMessages?.length,
      taskManagementsCount: taskManagements?.length
    });
    return {
      currentScenario: messageNodesWithAnswers.map((node) => ({
        label: (node.data as CustomMessageNodeData).message ?? '',
        value: (node.data as CustomMessageNodeData).message ?? '',
      })),
      // LF360-1938: grouped per scenario instead of one flat "Other scenarios"
      // bucket. The scenarioId was always present in the payload and simply
      // discarded here; the join against scenarioNames only supplies the label.
      otherScenarios: buildOtherScenarioGroups(
        allMessages as FetchAllMessagesPayload['data'],
        messageNodesWithAnswers.map((node) => (node.data as CustomMessageNodeData).message ?? ''),
        scenarioNames,
      ),
      taskManagements: (taskManagements as FetchTaskManagementsPayload['data']).map((taskManagement: {
        id: string;
        title: string;
        type: string;
        description: string;
      }) => ({
        label: taskManagement.title,
        value: taskManagement.id,
        isTaskManagement: true,
      })),
      metrics: [
        { label: 'Adherence (%)', value: ADHERENCE_METRIC_VALUE, isAdherence: true },
      ],
      // LF360-1828: the project's profile fields, fetched via useFetchProfileFields
      // (reuses fetchProfileManagement - see that hook's comment for the caveat on
      // what sourceId/message actually represent today).
      profileFields: profileFields.map((field) => ({
        label: field.label,
        value: field.id,
        isProfileField: true,
        fieldType: field.fieldType,
      })),
      // LF360-1828: the project's onboarding questions, fetched via
      // useFetchOnboardingQuestions (a purpose-built fetcher - see
      // useFetchOnboardingQuestions.hook.ts).
      onboardingResponses: onboardingQuestions.map((question) => ({
        label: question.label,
        value: question.id,
        isOnboardingResponse: true,
        fieldType: question.fieldType,
      })),
    };
  }, [messageNodesWithAnswers, allMessages, taskManagements, profileFields, onboardingQuestions, scenarioNames]);

  const getUpdatedSchema = getBehaviorRulesSchema({
    possibleQuestions: possibleQuestionsForSchema,
    possibleAnswers: []
  });

  interface FormValues {
    conditions: CustomIfConditionBehaviorNodeData['conditions'];
  }

  const defaultValues: FormValues = {
    conditions: selectedNodePropsData.conditions
      ?? [
        {
          rules: {
            operator: 'AND',
            criteria: [{
              question: '',
              value: '',
              operator: 'eq',
              period: 1
            }]
          }
        },
        {
          type: 'else',
        }
      ],
  };

  // Make sure isTaskManagement is properly set in the initial form data
  useEffect(() => {
    // Only run once on component mount
    if (selectedNodePropsData.conditions) {
      console.debug('Initializing with conditions:', selectedNodePropsData.conditions);
      console.debug('Available task managements:', taskManagements);

      // If existing conditions are loaded, ensure task management flags are set properly
      const updatedConditions = selectedNodePropsData.conditions.map(condition => {
        if ('rules' in condition && condition.rules.criteria) {
          return {
            ...condition,
            rules: {
              ...condition.rules,
              criteria: condition.rules.criteria.map(criteria => {
                // Find the matching task management item by ID
                const isTaskItem = taskManagements.some(task =>
                  task.id === criteria.question
                );

                const hadTaskFlag = Boolean(criteria.isTaskManagement);
                const shouldHaveTaskFlag = isTaskItem || hadTaskFlag;

                // If the flag status is changing, log for debugging
                if (shouldHaveTaskFlag !== hadTaskFlag) {
                  console.debug('Updating task flag for item:', {
                    question: criteria.question,
                    hadTaskFlag,
                    isTaskItem,
                    willHaveTaskFlag: shouldHaveTaskFlag
                  });
                }

                // Make sure the value is valid for tasks
                let value = criteria.value;
                if (shouldHaveTaskFlag && !isValidTaskStatus(value)) {
                  value = DEFAULT_TASK_STATUS;
                }

                // Make sure isTaskManagement flag is set if this is a task item
                // LF360-1810: seed a default window for task criteria that don't have one yet
                const resolvedWindow = shouldHaveTaskFlag ? (criteria.taskWindow ?? DEFAULT_TASK_WINDOW) : criteria.taskWindow;
                const isLastN = shouldHaveTaskFlag && resolvedWindow === 'last-n';
                // LF360-1812: adherence criteria carry threshold (percent) too;
                // don't let the task-only isLastN gate strip it.
                const isAdherenceCriterion = Boolean((criteria as BehaviorRuleStatementCurrent).isAdherence);
                return {
                  ...criteria,
                  isTaskManagement: shouldHaveTaskFlag,
                  value,
                  taskWindow: resolvedWindow,
                  // period is user-editable N for 'last-n'; forced to 1 for every
                  // other (single-instance) task window, untouched for messages.
                  period: shouldHaveTaskFlag
                    ? (isLastN ? (criteria.period && criteria.period >= 1 ? criteria.period : DEFAULT_LAST_N_PERIOD) : 1)
                    : (criteria.period || 1),
                  matchMode: isLastN ? (criteria.matchMode ?? DEFAULT_MATCH_MODE) : undefined,
                  threshold: (isLastN || isAdherenceCriterion) ? criteria.threshold : undefined,
                };
              })
            }
          };
        }
        return condition;
      });

      // Update the form if changes were needed
      if (JSON.stringify(updatedConditions) !== JSON.stringify(selectedNodePropsData.conditions)) {
        console.debug('Updating form with corrected task flags:', updatedConditions);
        setTimeout(() => {
          form.reset({ conditions: updatedConditions });
          // Also update the node data to persist these changes
          updateItem?.(selectedNodeProps?.id ?? '', { conditions: updatedConditions } as CustomIfConditionBehaviorNodeData);
        }, 0);
      } else {
        console.debug('No changes needed to task flags');
      }
    }
  }, [selectedNodeProps.id, taskManagements.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm<FormValues>({
    resolver: zodResolver(getUpdatedSchema),
    defaultValues,
  });

  const { fields, remove, insert, update } = useFieldArray({
    control: form.control,
    name: 'conditions',
  });

  const updateItem = updateNode({ reactFlowInstance: instance });

  useEffect(() => {
    const subscription = form.watch((_, { name, type }) => {
      // Skip processing during reordering operations
      if (type === 'change' && name?.includes('conditions') &&
          // This check helps prevent unnecessary updates during drag operations
          !document.querySelector('[data-rbd-dragging-id]')) {
        void form.handleSubmit(changeHandler)()
        const formValues = form.getValues();
        changeHandler(formValues);
        setFormValues(formValues.conditions);
      }
    });

    // enable initial fields validation
    form.trigger().catch(console.error);

    return () => subscription.unsubscribe();
  }, [form.handleSubmit, form.watch]);

  // Reset per-node state when switching to a different node
  useEffect(() => {
    // LF360-1933 round 2: what a message answered is only meaningful for the
    // node that asked. Returning prev when already empty keeps a node switch
    // from costing a render it does not need.
    setSettledMessages(prev => (prev.size === 0 ? prev : new Set<string>()));

    // Clear dropdown open state for this node
    setDropdownOpenState(prev => {
      const newMap = new Map(prev);
      const nodeIdPrefix = `${selectedNodeProps.id}-`;
      Array.from(newMap.keys()).forEach(key => {
        if (key.startsWith(nodeIdPrefix)) {
          newMap.delete(key);
        }
      });
      return newMap;
    });
  }, [selectedNodeProps.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark every row pending whenever the modal opens or the node changes. This
  // site had no index to narrow to and its whole-node reach is deliberate.
  useEffect(() => {
  }, [selectedNodeProps.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single source of truth for task statuses (LF360-1810).
  // value -> backend SubjectTask.status: pending=PENDING, completed=COMPLETED,
  // missed=INCOMPLETED, dismissed=DISMISSED, not-assigned = no row in window.
  // NOTE: the editor no longer emits legacy 'assigned-for-today'; the backend
  // still honors it for already-saved rules.
  const TASK_STATUS_OPTIONS: { label: string; value: string }[] = [
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' },
    { label: 'Missed', value: 'missed' },
    { label: 'Not assigned', value: 'not-assigned' },
    { label: 'Dismissed', value: 'dismissed' },
  ];
  const DEFAULT_TASK_STATUS = 'completed';

  // Task evaluation window (LF360-1810). Missing/unknown window defaults to
  // 'today' on the backend; the editor seeds it explicitly when a criterion
  // becomes a task so the choice is visible/editable.
  const TASK_WINDOW_OPTIONS: { label: string; value: 'today' | 'yesterday' | 'latest' | 'last-n' }[] = [
    { label: "Today's instance", value: 'today' },
    { label: 'Previous day', value: 'yesterday' },
    { label: 'Latest instance', value: 'latest' },
    { label: 'Last N instances', value: 'last-n' },
  ];
  const DEFAULT_TASK_WINDOW: 'today' | 'yesterday' | 'latest' | 'last-n' = 'today';
  // LF360-1810 (task-window extension): DEFAULT_LAST_N_PERIOD/DEFAULT_MATCH_MODE
  // are imported from lastNTaskWindow.ts (single source of truth, shared with
  // the pure seedTaskWindowSwitch() used below).
  const MATCH_MODE_OPTIONS: { label: string; value: 'all' | 'any' | 'at-least' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Any', value: 'any' },
    { label: 'At least', value: 'at-least' },
  ];

  // Add utility functions for task-related operations
  const getTaskStatuses = (): string[] => TASK_STATUS_OPTIONS.map((option) => option.value);

  const isValidTaskStatus = (status: string): boolean => {
    return getTaskStatuses().includes(status);
  };


  useEffect(() => {
    console.log('🔵 Effect 1: Processing form values triggered');
    const responsesByMessage: Record<string, string[]> = {};
    const responsesForMessagesToFetch: string[] = [];
    const tasksToFetch: string[] = [];

    // Log initial form values for debugging
    console.debug('Processing form values:', formValues);

    formValues.forEach((condition) => {
      if ('rules' in condition) {
        condition.rules.criteria.forEach((criteria) => {
          // Use the utility function to check if it's a task
          if (isTask(criteria)) {
            // For tasks, we don't need to fetch responses as they're predefined
            tasksToFetch.push(criteria.question);

            // Use the utility function to get task statuses
            responsesByMessage[criteria.question] = getTaskStatuses();

            // Log for debugging
            console.debug('Detected task criteria:', { question: criteria.question, isTaskManagement: criteria.isTaskManagement });
          } else if (criteria.isProfileField) {
            // LF360-1828: single/multi/picker profile fields need a dropdown of
            // the underlying field's own options. There's no dedicated endpoint
            // for that - reuse the same message-text-keyed options fetch that
            // regular message-response criteria use (fetchOptionsByMessage),
            // resolving the message text from the fetched profileFields list.
            const { profileFieldId, fieldType } = criteria;
            const messageLabel = profileFields.find((field) => field.id === profileFieldId)?.label;
            if (messageLabel && PROFILE_DROPDOWN_FIELD_TYPES.includes(fieldType ?? '')) {
              if (!responsesMap[messageLabel]) {
                responsesForMessagesToFetch.push(messageLabel);
              } else {
                responsesByMessage[messageLabel] = responsesMap[messageLabel];
              }
            }
          } else if (criteria.isOnboardingResponse) {
            // LF360-1828: unlike profile fields, onboarding questions carry their
            // own answer options directly in the fetchOnboardingQuestions()
            // payload (see useFetchOnboardingQuestions.hook.ts) - no
            // message-text-keyed fetch needed here. Falling through to the
            // regular-message `else` branch below would wrongly treat
            // criteria.question (the onboardingQuestionId) as a message and
            // queue a bogus fetchOptionsByMessage() call for it.
          } else {
            // Handle regular messages as before
            const messageNodeWithAnswers = messageNodesWithAnswers.find(
              (node) => (node.data as CustomMessageNodeData).message === criteria.question
            );

            if (messageNodeWithAnswers) {
              responsesByMessage[criteria.question] = (
                (messageNodeWithAnswers.data as CustomMessageNodeData).response ?? []
              ).map((response) => response.label);
            } else if (criteria.question && !responsesMap[criteria.question]) {
              // Only fetch if we don't already have the responses
              responsesForMessagesToFetch.push(criteria.question);
            } else if (criteria.question && responsesMap[criteria.question]) {
              // Use already fetched responses
              responsesByMessage[criteria.question] = responsesMap[criteria.question];
            }
          }
        });
      }
    });

    console.log('🔵 Effect 1: Setting responses', {
      responsesForMessagesToFetch,
      responsesByMessageKeys: Object.keys(responsesByMessage)
    });

    // Only update if there are actual changes to avoid infinite loops
    setResponseMessagesToFetchOptions(prev => {
      const hasChanged = JSON.stringify(prev) !== JSON.stringify(responsesForMessagesToFetch);
      if (hasChanged) {
        console.log('📝 Updating responseMessagesToFetchOptions', { prev, new: responsesForMessagesToFetch });
        return responsesForMessagesToFetch;
      }
      return prev;
    });

    setResponsesMap(prev => {
      // Merge with previous responses instead of replacing
      const merged = { ...prev, ...responsesByMessage };
      const hasChanged = JSON.stringify(prev) !== JSON.stringify(merged);
      if (hasChanged) {
        console.log('📝 Updating responsesMap', { prevKeys: Object.keys(prev), newKeys: Object.keys(merged) });
        return merged;
      }
      return prev;
    });
  }, [formValuesKey, profileFields]);

  useEffect(() => {
    console.log('🟢 Effect 2: Fetch responses triggered', {
      shouldFetch: responseMessagesToFetchOptions.length > 0,
      messagesToFetch: responseMessagesToFetchOptions
    });
    if (responseMessagesToFetchOptions.length) {
      fetchResponsesByMessage(responseMessagesToFetchOptions).catch(console.error);
    }
  }, [responseMessagesToFetchOptions]);

  // Add a separate effect for handling task-specific data if needed in the future
  useEffect(() => {
    // This function separates task criteria from regular message criteria
    const getTaskCriteria = () => {
      const taskCriteria: { id: string; question: string }[] = [];

      formValues.forEach((condition) => {
        if ('rules' in condition) {
          condition.rules.criteria.forEach((criteria) => {
            if (criteria.isTaskManagement && criteria.question) {
              taskCriteria.push({
                id: criteria.question,
                question: criteria.question
              });
            }
          });
        }
      });

      return taskCriteria;
    };

    // Store task criteria for potential future implementation
    // Currently logging for debugging purposes
    const taskCriteria = getTaskCriteria();
    if (taskCriteria.length > 0) {
      // We're getting the task criteria but not using them yet
      // This is a placeholder for future implementation
      console.debug('Task criteria available for future implementation:', taskCriteria);
    }

    // For now, we're just using the predefined task statuses in the above effect,
    // but this hook allows for future extension to fetch dynamic task statuses or
    // other task-related data if requirements change

    // Example of potential future implementation:
    // if (taskCriteria.length) {
    //   fetchTaskStatuses(taskCriteria).catch(console.error);
    // }
  }, [formValues]);

  useEffect(() => {
    if (isFetching === true || isFetchingTaskManagements === true || isFetching === null) {
      return;
    }

    // Extract node message values
    const nodeMessages = (messageNodesWithAnswers ?? [])
      .map(item => (item.data as CustomMessageNodeData).message)
      .filter(Boolean) as string[];

    // Extract all messages
    const messages = (allMessages as { message: string }[])
      .map(item => item.message)
      .filter(Boolean);

    // Extract task titles
    const taskTitles = (taskManagements as { title: string; id: string }[])
      .map(item => item.id)
      .filter(Boolean);

    // Create a tuple with at least one element if available, or empty array
    const possibleQuestionsArray = [...nodeMessages, ...messages, ...taskTitles];
    const possibleQuestions = possibleQuestionsArray.length
      ? (possibleQuestionsArray as [string, ...string[]])
      : ([] as unknown as [string, ...string[]]);

    const getUpdatedSchema = getBehaviorRulesSchema({
      possibleQuestions,
      possibleAnswers: []
    });

    validatorInstance.addZodRule(`if:condition-behavior:${selectedNodeProps?.id}`, getUpdatedSchema);
  }, [messageNodesWithAnswers, allMessages, isFetching, isFetchingTaskManagements, taskManagements])

  async function fetchResponsesByMessage(responseMessagesToFetchOptions: string[]) {
    console.log('🔴 fetchResponsesByMessage called', { messages: responseMessagesToFetchOptions });
    if (!responseMessagesToFetchOptions.length) return;

    try {
      const responses = await Promise.all(
        responseMessagesToFetchOptions.map((message) => fetchContext.fetchOptionsByMessage(message)),
      );

      const responsesData = responses.map((response) => response.data);
      const responsesByMessage: Record<string, string[]> = {};

      console.log('>>>responsesData', responsesData);

      responsesData.forEach(({ message, options }) => {
        responsesByMessage[message] = options;
      });

      console.log('🔴 fetchResponsesByMessage completed, updating responsesMap', {
        newKeys: Object.keys(responsesByMessage)
      });
      setResponsesMap(prevResponses => ({
        ...prevResponses,
        ...responsesByMessage,
      }));
    } catch (error) {
      console.error('Error fetching response options:', error);
    } finally {
      // LF360-1933 round 2: settle what was ASKED for, in a finally rather than
      // after the await. A failed fetch has also finished waiting, and a row
      // left pending on a rejected request is a control disabled for the rest
      // of the session with a spinner beside it - the error path is the one
      // that most needs this to run.
      setSettledMessages(prev => {
        if (responseMessagesToFetchOptions.every(message => prev.has(message))) return prev;
        const next = new Set(prev);
        responseMessagesToFetchOptions.forEach(message => next.add(message));
        return next;
      });
    }
  }

  const changeHandler = useCallback((data: FormValues) => {
    // Make sure isTaskManagement flag is properly set for task items
    const updatedConditions = data.conditions.map(condition => {
      if ('rules' in condition && condition.rules.criteria) {
        return {
          ...condition,
          rules: {
            ...condition.rules,
            criteria: condition.rules.criteria.map(criteria => {
              // Check if this is a task by ID
              const isTaskItem = taskManagements.some(task =>
                task.id === criteria.question
              );

              const isTaskManagement = isTaskItem || Boolean(criteria.isTaskManagement);

              // For tasks, ensure the value is valid
              let value = criteria.value;
              if (isTaskManagement && !isValidTaskStatus(value)) {
                // Set a default value if the current one isn't valid
                value = DEFAULT_TASK_STATUS;
              }

              // Ensure isTaskManagement is set correctly
              // LF360-1810: seed a default window for task criteria that don't have one yet
              const resolvedWindow = isTaskManagement ? (criteria.taskWindow ?? DEFAULT_TASK_WINDOW) : criteria.taskWindow;
              const isLastN = isTaskManagement && resolvedWindow === 'last-n';
              // LF360-1812: adherence criteria carry threshold (percent) too;
              // don't let the task-only isLastN gate strip it.
              const isAdherenceCriterion = Boolean((criteria as BehaviorRuleStatementCurrent).isAdherence);
              return {
                ...criteria,
                isTaskManagement,
                value,
                taskWindow: resolvedWindow,
                // period is user-editable N for 'last-n'; forced to 1 for every
                // other (single-instance) task window, untouched for messages.
                period: isTaskManagement
                  ? (isLastN ? (criteria.period && criteria.period >= 1 ? criteria.period : DEFAULT_LAST_N_PERIOD) : 1)
                  : (criteria.period || 1),
                matchMode: isLastN ? (criteria.matchMode ?? DEFAULT_MATCH_MODE) : undefined,
                threshold: (isLastN || isAdherenceCriterion) ? criteria.threshold : undefined,
              };
            })
          }
        };
      }
      return condition;
    });

    // Log updated data for debugging
    console.debug('Saving updated data with corrected task properties:', updatedConditions);

    // Update with the corrected data
    const updatedData = { ...data, conditions: updatedConditions };
    updateItem?.(selectedNodeProps?.id ?? '', updatedData as CustomIfConditionBehaviorNodeData);
    setTimeout(() => {
      setBranchUpdaterKey(() => (Math.random()));
    }, 500);
  }, [taskManagements, selectedNodeProps.id, updateItem, setBranchUpdaterKey]);

  const removeStatementHandler = useCallback((index: number) => {
    // Clean up dropdown open states for this statement's criteria
    const field = fields[index];
    const criteriaCount = field && 'rules' in field ? field.rules.criteria.length : 0;
    if (field && 'rules' in field) {
      setDropdownOpenState(prev => {
        const newMap = new Map(prev);
        for (let i = 0; i < criteriaCount; i++) {
          ['condition', 'response', 'operator'].forEach(type => {
            newMap.delete(getDropdownKey(index, i, type as 'condition' | 'response' | 'operator'));
          });
        }
        return newMap;
      });
    }

    // LF360-1933: removing a branch reflows that branch's rules and nothing

    const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
    const { updatedNodes, updatedEdges } = deleteStatementBranch(reactFlowInstance.get(reactFlowInstanceAtom)!, selectedNodeProps, index ) ?? {};
    const nodesToSave = updatedNodes!.map((node: CustomNode) =>
      node.id === selectedNodeProps.id
        ? ({
          ...node,
          data: {
            ...(node.data as CustomNodeData),
            conditions: fields.filter((_, i) => i !== index)
          }
        })
        : node
    );

    remove(index);

    recalculateNodesXPos(nodesToSave, wrapperSize.x, { instance });
    recalculateNodesYPosForNodes(nodesToSave, updatedEdges!);
    recalculateHandlePositions(nodesToSave, updatedEdges!);

    instance.setNodes(() => nodesToSave);
    instance.setEdges(() => updatedEdges!);

    setTimeout(() => {
      setBranchUpdaterKey(() => (Math.random()));
    }, 500);
  }, [fields, selectedNodeProps, setDropdownOpenState, reactFlowInstance, wrapperSize.x, remove, setBranchUpdaterKey]);

  const addBranchHandler = useCallback(() => {
    const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
    const newCondition = {
      rules: {
        operator: 'AND' as const,
        criteria: [{
          question: '',
          value: '',
          operator: 'eq' as Operator,
          period: 1
        }]
      }
    };
    const { updatedNodes, updatedEdges } = addStatementBranch(reactFlowInstance.get(reactFlowInstanceAtom)!, selectedNodeProps) ?? {};

    insert(fields.length - 1, newCondition)

    const nodesToSave = updatedNodes.map((node: CustomNode) =>
      node.id === selectedNodeProps.id
        ? ({
          ...node,
          data: {
            ...(node.data as CustomNodeData),
            // add before last (Else) statement
            conditions: [...fields.slice(0, -1), newCondition, ...fields.slice(-1)]
          }
        })
        : node
    );

    instance.setNodes(() => nodesToSave);
    instance.setEdges(() => updatedEdges);

    recalculateNodesXPos(nodesToSave, wrapperSize.x, { instance });
    recalculateNodesYPosForNodes(nodesToSave, updatedEdges);
    recalculateHandlePositions(nodesToSave, updatedEdges);

    setTimeout(() => {
      setBranchUpdaterKey(() => (Math.random()));
    }, 100);

    // LF360-1933: the new branch is inserted before the trailing Else and
  }, [fields, selectedNodeProps, reactFlowInstance, wrapperSize.x, insert, setBranchUpdaterKey]);

  const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
    return <Droppable {...props}>{children}</Droppable>;
  };

  return (
    <div className="text-sm flex-col flex w-full relative">
      {/* LF360-1933 (BRM-03): a full-bleed absolutely-positioned, blurred,
          high-stacking-index overlay used to sit here and cover the whole panel
          body while ANY single rule was settling, which is what made adding a
          second rule feel like the modal had frozen. The affordance now lives
          inside the row it is actually reporting on - see
          rowPendingSpinnerClasses() below. Its class names are deliberately not
          quoted here: the acceptance grep for this change is a literal search
          of this file for them, and spelling them in a comment would defeat it.
          The `relative` on this wrapper stays: the row trash and the branch drag
          handle anchor to their own positioned ancestors, not to this one, so
          removing the overlay does not free it. */}
      <Form {...form}>
        {/* LF360-1817: bound the panel body height and scroll it. The node-info
            panel is fixed/draggable with no scroll of its own, so tall content
            (many branches, the Else row at the bottom) ran off-viewport and was
            unreachable. `relative` keeps the absolute drag handles anchored here so
            they scroll with their row; all dropdowns portal to <body>, so this
            overflow container never clips them. `pl-2` gives the -ml drag handle a
            gutter so overflow-x doesn't clip it. */}
        <div className="relative max-h-[70vh] overflow-y-auto pl-2">
        <DragDropContext onDragEnd={(result) => {
          const resultDestination = result?.destination;
          if (!resultDestination) return;

          const destinationField = fields[resultDestination?.index];
          if ((destinationField as ElseCondition)?.type === 'else') return;

          // Store indices
          const sourceIndex = result.source.index;
          const destIndex = resultDestination.index;

          // Move the field using the useFieldArray swap method
          // This preserves the field values during reordering
          fields.forEach((_, i) => {
            if (i !== sourceIndex && i !== destIndex) return;
          });

          // First remove the source item
          const sourceItem = form.getValues().conditions[sourceIndex];
          remove(sourceIndex);

          // Then insert it at the destination
          insert(destIndex, sourceItem);

          // Get the updated items after the move operation
          const updatedItems = form.getValues().conditions;

          // Now update the React Flow nodes with the updated items
          const reorderResult = reorderStatementBranch({
            instance: reactFlowInstance.get(reactFlowInstanceAtom)!,
            node: selectedNodeProps,
            branchIndex: sourceIndex,
            newPositionIndex: destIndex
          });

          if (reorderResult) {
            const { updatedNodes } = reorderResult;
            const nodesToSave = updatedNodes.map(node => {
              const matchedNode = node.id === selectedNodeProps?.id;
              return matchedNode
                ? ({
                  ...node,
                  data: {
                    ...(node.data as CustomNodeData),
                    conditions: updatedItems
                  }
                })
                : node
            });

            setTimeout(() => {
              instance.setNodes(() => nodesToSave);
              instance.setEdges(() => edges);
            }, 100);
          }
        }}>
          <StrictModeDroppable droppableId="droppable">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {fields.slice(0, -1).map((field, index) => (
                  <Draggable key={field.id} draggableId={field.id} index={index} isDragDisabled={disabled}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={cn('group relative w-full', branchBlockClasses())}
                        style={{
                          ...provided.draggableProps.style,
                          left: undefined,
                          top: undefined,
                        }}
                      >
                        {!disabled && (
                          <div
                            {...provided.dragHandleProps}
                            className={cn('cursor-move absolute -ml-[24px] mt-[5px]', revealOnHoverClasses())}
                          >
                            <Icon type="drag-handler" className="mr-2" />
                          </div>
                        )}
                        <BranchHeader index={index} />
                        <div className="grow">
                          <Controller
                            key={field.id}
                            control={form.control}
                            name={`conditions.${index}`}
                            render={({ field, fieldState }) => {
                              const { onChange, value } = field;
                              // already selected values by previous statements
                              const allCriteria = (value as RulesCondition)?.rules?.criteria;
                              const criteriaToRender = allCriteria?.length
                                ? allCriteria
                                : [{ type: 'current', question: '', value: '', operator: 'eq' as Operator, period: 1 }];

                              const valueChangeHandler = ({newValue, period, criteriaIndex, isTaskManagement}: {
                                newValue: string,
                                period: number,
                                criteriaIndex:
                                number,
                                isTaskManagement: boolean
                              }) => {
                                onChange({
                                  ...value,
                                  rules: {
                                    ...(value as RulesCondition)?.rules,
                                    criteria: criteriaToRender.map((criteria, i) => {
                                      if (i === criteriaIndex) {
                                        // LF360-1810: when a criterion becomes a task, seed a valid
                                        // status + window; otherwise clear value for the new message selection
                                        const nextWindow = isTaskManagement
                                          ? ((criteria as BehaviorRuleStatementCurrent).taskWindow ?? DEFAULT_TASK_WINDOW)
                                          : undefined;
                                        const isLastN = isTaskManagement && nextWindow === 'last-n';
                                        return {
                                          ...criteria,
                                          question: newValue,
                                          isTaskManagement,
                                          // LF360-1817: this path only runs for task/answer selections
                                          // (the adherence/profile-field/onboarding-response branches
                                          // return earlier). Clear every adherence-only, profile-only
                                          // and onboarding-only field so their rows stop rendering -
                                          // isAdherence/windowDays (and now isProfileField/profileFieldId/
                                          // isOnboardingResponse/onboardingQuestionId/fieldType,
                                          // LF360-1828) used to stick and strand those inputs (and their
                                          // operator sets) on a task/answer criterion.
                                          isAdherence: false,
                                          windowDays: undefined,
                                          isProfileField: false,
                                          profileFieldId: undefined,
                                          isOnboardingResponse: false,
                                          onboardingQuestionId: undefined,
                                          fieldType: undefined,
                                          operator: 'eq' as Operator,
                                          value: isTaskManagement
                                            ? (isValidTaskStatus(criteria.value) ? criteria.value : DEFAULT_TASK_STATUS)
                                            : '',
                                          taskWindow: nextWindow,
                                          // period is user-editable N for 'last-n'; forced to 1 for every
                                          // other (single-instance) task window, untouched for messages.
                                          period: isTaskManagement ? (isLastN ? period : 1) : period,
                                          matchMode: isLastN ? ((criteria as BehaviorRuleStatementCurrent).matchMode ?? DEFAULT_MATCH_MODE) : undefined,
                                          threshold: isLastN ? (criteria as BehaviorRuleStatementCurrent).threshold : undefined,
                                        }
                                      }
                                      return criteria;
                                    })
                                  }
                          })

                          // Ensure fetch happens even with re-renders — but NOT for a
                          // message that is a current-scenario node. Those already carry
                          // their answer options in node.data.response (resolved locally
                          // by the responsesMap effect); the backend /question-responses
                          // lookup returns [] for them, and letting that empty result land
                          // wipes the local options ("No options available"). LF360-1817.
                          const isCurrentScenarioMessage = messageNodesWithAnswers.some(
                            (node) => (node.data as CustomMessageNodeData).message === newValue
                          );
                          if (!isTaskManagement && newValue && !isCurrentScenarioMessage && !responsesMap[newValue]) {
                            fetchResponsesByMessage([newValue]).catch(console.error);
                          }
                        }

                        return (
                          <div className="grow">
                            {/* LF360-1933: this wrapper, not branchBlockClasses(), is what actually
                                separates one rule from the next - branchBlockClasses() gaps the branch
                                header from the rules area, one level up. It was 12px against a Figma
                                that draws 8px, so correcting only the factory would have left the
                                rendered rule-to-rule spacing untouched. Figma node 7643:34271: the rule
                                column stacks its frames at y=0(h31), 39, 78. */}
                            <div className="flex flex-col gap-2">
                              {(value as RulesCondition)?.rules && criteriaToRender.map((criteria, criteriaIndex) => {
                              const criteriaErrorObj = (fieldState?.error as CriteriaError)?.rules?.criteria?.[criteriaIndex];
                              const hasError = Object.keys(criteriaErrorObj ?? {}).length > 0;
                              const errorMessage = ['question', 'value', 'period'].map((key) => criteriaErrorObj?.[key as CriteriaErrorProps]?.message).filter(Boolean).join(', ');

                              // Determine if this is a task by checking both the flag and the ID match
                              const isTaskById = taskManagements.some(task => task.id === criteria.question);
                              const hasTaskFlag = Boolean((criteria as BehaviorRuleStatementCurrent).isTaskManagement);
                              const isTask = isTaskById || hasTaskFlag;
                              const isAdherence = Boolean((criteria as BehaviorRuleStatementCurrent).isAdherence);
                              // LF360-1828: Participant Profile criterion. fieldType (short form of the
                              // source field's inputType) drives both the operator set and the value
                              // control below. fieldType is a single shared column also used by the
                              // Onboarding Responses criterion below - profileFieldType is read once
                              // here and reused for both (the name is kept from Task 4).
                              const isProfileField = Boolean((criteria as BehaviorRuleStatementCurrent).isProfileField);
                              const profileFieldType = (criteria as BehaviorRuleStatementCurrent).fieldType;
                              const profileFieldOption = comboboxOptions.profileFields.find(
                                (option) => option.value === (criteria as BehaviorRuleStatementCurrent).profileFieldId
                              );
                              // Dropdown fieldTypes (single/multi/picker) don't carry their own options
                              // in fetchProfileManagement()'s payload - reuse the message-response fetch
                              // (responsesMap), keyed by the bound node's message text (the option's label).
                              const profileFieldValueOptions = (profileFieldOption && PROFILE_DROPDOWN_FIELD_TYPES.includes(profileFieldType ?? ''))
                                ? (responsesMap[profileFieldOption.label] ?? []).map((response: string) => ({ label: response, value: response }))
                                : [];
                              // LF360-1828: Onboarding Responses criterion. Same fieldType-driven
                              // operator set as Participant Profile (getProfileFieldOperatorOptions,
                              // reused as-is), but the ONE structural difference: onboarding questions
                              // carry their own answer options directly in the fetch payload (see
                              // useFetchOnboardingQuestions.hook.ts), so there is no responsesMap
                              // indirection to look up here.
                              const isOnboardingResponse = Boolean((criteria as BehaviorRuleStatementCurrent).isOnboardingResponse);
                              const onboardingQuestionRecord = onboardingQuestions.find(
                                (question) => question.id === (criteria as BehaviorRuleStatementCurrent).onboardingQuestionId
                              );
                              const onboardingResponseValueOptions = (onboardingQuestionRecord && PROFILE_DROPDOWN_FIELD_TYPES.includes(profileFieldType ?? ''))
                                ? (onboardingQuestionRecord.options ?? []).map((response: string) => ({ label: response, value: response }))
                                : [];
                              // LF360-1810 (task-window extension): the N/match-mode/K fields only
                              // apply to the 'last-n' window.
                              const currentTaskWindow = (criteria as BehaviorRuleStatementCurrent).taskWindow ?? DEFAULT_TASK_WINDOW;
                              const isLastNWindow = isTask && currentTaskWindow === 'last-n';
                              const currentMatchMode = (criteria as BehaviorRuleStatementCurrent).matchMode ?? DEFAULT_MATCH_MODE;

                              // Set response options based on whether this is a task
                              let responseOptions = [];

                              if (isTask) {
                                // For tasks, use predefined status options (LF360-1810)
                                responseOptions = TASK_STATUS_OPTIONS;
                              } else {
                                // For messages, use the fetched options
                                responseOptions = [
                                  ...(responsesMap[criteria.question] ?? [])
                                    .map((response: string) => ({label: response, value: response}))
                                ];
                              }

                              // LF360-1933 round 2: hoisted out of the JSX because the
                              // render below now has to ask how many options there are
                              // before it decides what kind of control to draw. Same
                              // three-way choice as before, moved, not changed.
                              const operatorOptions: { label: string; value: string }[] =
                                isAdherence
                                  ? ADHERENCE_OPERATOR_OPTIONS
                                  : (isProfileField || isOnboardingResponse)
                                    ? getProfileFieldOperatorOptions(profileFieldType)
                                    : [{ label: '=', value: 'eq' }];

                              return (
                                <Fragment key={criteria.value}>
                                <div
                                  className={cn(criteriaRowClasses(), 'group/row', rowDeleteHighlightClasses())}
                                >
                                  {hasError && shouldShowErrorWarnings && (
                                    <CustomTooltip content={errorMessage}>
                                      <ExclamationTriangleIcon className="size-4 text-destructive" />
                                    </CustomTooltip>
                                  )}
                                  {/* LF360-1933 (BRM-03): the loading affordance, inside the row it
                                      reports on. It is a child of the criteria row wrapper rather
                                      than a sibling of the panel body precisely so it cannot cover
                                      anything but this rule - the overlay it replaced could, and
                                      did. */}
                                  {isRowShowingIndicator(index, criteriaIndex) && (
                                    <span
                                      className={rowPendingSpinnerClasses()}
                                      data-testid="info=>behavior:rule:pending"
                                      role="status"
                                      aria-label="Rule is loading"
                                    />
                                  )}
                                  <div data-testid="info=>behavior:rule">
                                    <CustomTooltip
                                      content={isRowPending(index, criteriaIndex) ? "Loading the answers for this message" : ""}
                                      disabled={!isRowPending(index, criteriaIndex)}
                                    >
                                      <ComboboxBehavior
                                        variant="pill"
                                        key={`condition-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                        value={{criteriaValue: criteria.question, period: criteria?.period ?? 1, isTaskManagement: Boolean((criteria as BehaviorRuleStatementCurrent).isTaskManagement)}}
                                        onValueChange={(newValue, params = {}) => {
                                          const period = params.period as number;

                                          // LF360-1812: the Metrics group emits isAdherence. Seed the
                                          // subject-level adherence criterion and clear task/message fields.
                                          if (params.isAdherence) {
                                            const existing = criteria as BehaviorRuleStatementCurrent;
                                            const nextOperator = ['gt', 'lt', 'gte', 'lte'].includes(existing.operator)
                                              ? existing.operator
                                              : DEFAULT_ADHERENCE_OPERATOR;
                                            onChange({
                                              ...value,
                                              rules: {
                                                ...(value as RulesCondition)?.rules,
                                                criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? {
                                                  ...c,
                                                  question: newValue,
                                                  isAdherence: true,
                                                  isTaskManagement: false,
                                                  // LF360-1828: clear any leftover Participant Profile /
                                                  // Onboarding Responses fields so their operator set/value
                                                  // control stop rendering (same class of stranding bug the
                                                  // LF360-1817 comment above describes).
                                                  isProfileField: false,
                                                  profileFieldId: undefined,
                                                  isOnboardingResponse: false,
                                                  onboardingQuestionId: undefined,
                                                  fieldType: undefined,
                                                  operator: nextOperator,
                                                  threshold: (c as BehaviorRuleStatementCurrent).threshold ?? DEFAULT_ADHERENCE_THRESHOLD,
                                                  windowDays: (c as BehaviorRuleStatementCurrent).windowDays ?? DEFAULT_ADHERENCE_WINDOW_DAYS,
                                                  value: '',
                                                  taskWindow: undefined,
                                                  matchMode: undefined,
                                                  period: c.period ?? 1,
                                                } : c),
                                              },
                                            });
                                            setDropdownOpen(index, criteriaIndex, 'condition', false);
                                            return;
                                          }

                                          // LF360-1828: the Participant Profile group emits isProfileField +
                                          // fieldType. Seed the profile-field criterion and clear task/message/
                                          // adherence/onboarding-response fields.
                                          if (params.isProfileField) {
                                            const existing = criteria as BehaviorRuleStatementCurrent;
                                            const fieldType = params.fieldType;
                                            const validOperators = getProfileFieldOperatorOptions(fieldType).map((option) => option.value);
                                            const nextOperator = (existing.isProfileField && existing.fieldType === fieldType && validOperators.includes(existing.operator))
                                              ? existing.operator
                                              : DEFAULT_PROFILE_FIELD_OPERATOR;
                                            onChange({
                                              ...value,
                                              rules: {
                                                ...(value as RulesCondition)?.rules,
                                                criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? {
                                                  ...c,
                                                  question: newValue,
                                                  isProfileField: true,
                                                  profileFieldId: newValue,
                                                  fieldType,
                                                  isTaskManagement: false,
                                                  isAdherence: false,
                                                  // LF360-1828: clear any leftover Onboarding Responses fields
                                                  // (same stranding-bug rationale as elsewhere in this file).
                                                  isOnboardingResponse: false,
                                                  onboardingQuestionId: undefined,
                                                  threshold: undefined,
                                                  windowDays: undefined,
                                                  operator: nextOperator,
                                                  value: '',
                                                  taskWindow: undefined,
                                                  matchMode: undefined,
                                                  period: c.period ?? 1,
                                                } : c),
                                              },
                                            });
                                            // Ensure the dropdown-fieldType options fetch happens even with
                                            // re-renders (same rationale as the "Ensure fetch happens..."
                                            // comment below, LF360-1817) - keyed by the bound node's message
                                            // text, since fetchProfileManagement() carries no options itself.
                                            const selectedOption = comboboxOptions.profileFields.find((option) => option.value === newValue);
                                            if (selectedOption && PROFILE_DROPDOWN_FIELD_TYPES.includes(fieldType ?? '') && !responsesMap[selectedOption.label]) {
                                              fetchResponsesByMessage([selectedOption.label]).catch(console.error);
                                            }
                                            setDropdownOpen(index, criteriaIndex, 'condition', false);
                                            return;
                                          }

                                          // LF360-1828: the Onboarding Responses group emits
                                          // isOnboardingResponse + fieldType. Seed the onboarding-response
                                          // criterion and clear task/message/adherence/profile-field fields.
                                          // Unlike the Participant Profile branch above, no follow-up fetch
                                          // is needed here - fetchOnboardingQuestions() already carries each
                                          // question's own answer options (useFetchOnboardingQuestions.hook.ts),
                                          // so a dropdown fieldType (single/multi/picker) has its options as
                                          // soon as the question is selected.
                                          if (params.isOnboardingResponse) {
                                            const existing = criteria as BehaviorRuleStatementCurrent;
                                            const fieldType = params.fieldType;
                                            const validOperators = getProfileFieldOperatorOptions(fieldType).map((option) => option.value);
                                            const nextOperator = (existing.isOnboardingResponse && existing.fieldType === fieldType && validOperators.includes(existing.operator))
                                              ? existing.operator
                                              : DEFAULT_PROFILE_FIELD_OPERATOR;
                                            onChange({
                                              ...value,
                                              rules: {
                                                ...(value as RulesCondition)?.rules,
                                                criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? {
                                                  ...c,
                                                  question: newValue,
                                                  isOnboardingResponse: true,
                                                  onboardingQuestionId: newValue,
                                                  fieldType,
                                                  isTaskManagement: false,
                                                  isAdherence: false,
                                                  isProfileField: false,
                                                  profileFieldId: undefined,
                                                  threshold: undefined,
                                                  windowDays: undefined,
                                                  operator: nextOperator,
                                                  value: '',
                                                  taskWindow: undefined,
                                                  matchMode: undefined,
                                                  period: c.period ?? 1,
                                                } : c),
                                              },
                                            });
                                            setDropdownOpen(index, criteriaIndex, 'condition', false);
                                            return;
                                          }

                                        // Check if the selected option is a task by ID, which is the most reliable method
                                        const isExplicitlyTask = params.isTaskManagement as boolean;
                                        // Double-check if this value is a task ID even if not explicitly marked
                                        const isTaskById = taskManagements.some(task => task.id === newValue);
                                        const isTaskManagement = isExplicitlyTask || isTaskById;

                                        // When task management is selected, period is forced to 1 unless the
                                        // criterion is already on the 'last-n' window, where period is the
                                        // user-editable N and must be preserved across question changes.
                                        const currentTaskWindow = (criteria as BehaviorRuleStatementCurrent).taskWindow;
                                        const isLastN = isTaskManagement && currentTaskWindow === 'last-n';
                                        const finalPeriod = isTaskManagement ? (isLastN ? (criteria.period ?? DEFAULT_LAST_N_PERIOD) : 1) : period;

                                        console.log('Selected value info:', {
                                          newValue,
                                          isExplicitlyTask,
                                          isTaskById,
                                          finalIsTask: isTaskManagement
                                        });

                                        valueChangeHandler({
                                          newValue,
                                          period: finalPeriod,
                                          criteriaIndex,
                                          isTaskManagement
                                        });


                                        // Close the dropdown after value selection
                                        setDropdownOpen(index, criteriaIndex, 'condition', false);
                                      }}
                                        options={comboboxOptions}
                                        placeholder="Select a condition..."
                                        disabled={disabled || isRowPending(index, criteriaIndex)}
                                        open={isDropdownOpen(index, criteriaIndex, 'condition')}
                                        onOpenChange={(open) => setDropdownOpen(index, criteriaIndex, 'condition', open)}
                                      />
                                    </CustomTooltip>
                                  </div>
                                  {/* LF360-1933 round 2: the operator and the response are two
                                      separate pills, 4px apart.

                                      The first round drew them as one shell split by a hairline,
                                      following Figma node 7643:34271. Reviewed on the demo, that
                                      was called the wrong way round and separate pills were asked
                                      for instead - so the shell, its divider and the shell-less
                                      Selectbox variant that existed only to sit inside it are all
                                      gone rather than left switched off.

                                      No wrapper of their own: as siblings in the criteria row they
                                      take the row's own 4px gap, which is the same gap that already
                                      separates them from the condition pill. A wrapper would have
                                      to restate that spacing and could then disagree with it. */}
                                  <>
                                      {/* LF360-1933 round 2: an operator with one option is a
                                          statement, not a choice. A plain message criterion only
                                          ever offers `=`, and a profile / onboarding field whose
                                          type has no entry in PROFILE_FIELD_OPERATOR_OPTIONS falls
                                          back to a lone `is`; both drew a chevron that opened a menu
                                          holding the single value already on screen.

                                          The operator stays visible and in place - it is a word in
                                          the sentence the rule reads as, not chrome - and only stops
                                          advertising a choice it cannot offer. Safe to render as
                                          text because every criterion is created with
                                          `operator: 'eq'` (see the four literals above), so the
                                          value the label states is already the value stored: there
                                          is nothing a user could have picked here that would have
                                          had to be written back. */}
                                      {operatorOptions.length <= 1 ? (
                                        <span
                                          className={staticPillClasses({ disabled: !criteria.question || disabled })}
                                          data-testid="info=>behavior:rule:operator-static"
                                        >
                                          {operatorOptions[0]?.label ?? criteria.operator}
                                        </span>
                                      ) : (
                                      <Selectbox
                                        variant="pill"
                                        key={`operator-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                        value={criteria.operator}
                                        options={operatorOptions}
                                        disabled={!criteria.question || disabled}
                                        onValueChange={(newValue) => {
                                          onChange({
                                            ...value,
                                            rules: {
                                              ...(value as RulesCondition)?.rules,
                                              criteria: criteriaToRender.map((c, i) => {
                                                if (i === criteriaIndex) {
                                                  return {
                                                    ...c,
                                                    operator: newValue,
                                                  }
                                                }
                                                return c;
                                              })
                                            }
                                          });
                                          // Close the dropdown after value selection
                                          setDropdownOpen(index, criteriaIndex, 'operator', false);
                                        }}
                                        className={cn("w-auto", { 'opacity-50': disabled })}
                                        open={isDropdownOpen(index, criteriaIndex, 'operator')}
                                        onOpenChange={(open) => setDropdownOpen(index, criteriaIndex, 'operator', open)}
                                      />
                                      )}
                                      {!isAdherence && !isProfileField && !isOnboardingResponse && (
                                        <>
                                        <CustomTooltip
                                          content={isRowPending(index, criteriaIndex) ? "Loading the answers for this message" : ""}
                                          disabled={!isRowPending(index, criteriaIndex)}
                                        >
                                          <Selectbox
                                            variant="pill"
                                            key={`response-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                            options={responseOptions}
                                            value={criteria.value}
                                            disabled={!criteria.question || disabled || isRowPending(index, criteriaIndex)}
                                            onValueChange={(newValue) => {
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => {
                                                    if (i === criteriaIndex) {
                                                      return {
                                                        ...c,
                                                        value: newValue
                                                      }
                                                    }
                                                    return c;
                                                  })
                                                }
                                              });


                                              // Close the dropdown after value selection
                                              setDropdownOpen(index, criteriaIndex, 'response', false);
                                            }}
                                            placeholder={(criteria as BehaviorRuleStatementCurrent)?.isTaskManagement ? 'Select status...' : 'Select response...'}
                                            className="w-auto min-w-5 max-w-60"
                                            open={isDropdownOpen(index, criteriaIndex, 'response')}
                                            onOpenChange={(open) => setDropdownOpen(index, criteriaIndex, 'response', open)}
                                          />
                                        </CustomTooltip>
                                        </>
                                      )}
                                    </>
                                    {!isTask && !isAdherence && !isProfileField && !isOnboardingResponse && criteria.question && (
                                      <div className="flex items-center gap-2" data-testid="info=>behavior:rule:answers-window">
                                        <span className="text-sm text-muted-foreground">over the last</span>
                                        <Input
                                          type="number"
                                          min={1}
                                          step={1}
                                          value={String(criteria.period ?? DEFAULT_ANSWERS_WINDOW)}
                                          disabled={!criteria.question || disabled}
                                          onChange={(e) => {
                                            const nextPeriod = clampAnswersWindow(e.target.value);
                                            onChange({
                                              ...value,
                                              rules: {
                                                ...(value as RulesCondition)?.rules,
                                                criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, period: nextPeriod } : c),
                                              },
                                            });
                                          }}
                                          className={cn(rulePillClasses(), "w-16")}
                                        />
                                        <span className="text-sm text-muted-foreground">answers</span>
                                      </div>
                                    )}
                                    {isAdherence && (
                                      <>
                                        <div className="flex items-center gap-2" data-testid="info=>behavior:rule:adherence:threshold">
                                          <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={String((criteria as BehaviorRuleStatementCurrent).threshold ?? DEFAULT_ADHERENCE_THRESHOLD)}
                                            disabled={disabled}
                                            onChange={(e) => {
                                              // Percent 0-100; truncate fractional input and clamp.
                                              const parsed = Math.trunc(Number(e.target.value));
                                              const nextThreshold = Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : DEFAULT_ADHERENCE_THRESHOLD;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, threshold: nextThreshold } : c),
                                                },
                                              });
                                            }}
                                            className={cn(rulePillClasses(), "w-16")}
                                          />
                                          <span className="text-sm text-muted-foreground">%</span>
                                        </div>
                                        <div className="flex items-center gap-2" data-testid="info=>behavior:rule:adherence:window">
                                          <span className="text-sm text-muted-foreground">over last</span>
                                          <Input
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={String((criteria as BehaviorRuleStatementCurrent).windowDays ?? DEFAULT_ADHERENCE_WINDOW_DAYS)}
                                            disabled={disabled}
                                            onChange={(e) => {
                                              const parsed = Math.trunc(Number(e.target.value));
                                              const nextWindow = Number.isFinite(parsed) && parsed >= 1 ? parsed : DEFAULT_ADHERENCE_WINDOW_DAYS;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, windowDays: nextWindow } : c),
                                                },
                                              });
                                            }}
                                            className={cn(rulePillClasses(), "w-16")}
                                          />
                                          <span className="text-sm text-muted-foreground">days</span>
                                        </div>
                                      </>
                                    )}
                                    {isProfileField && (
                                      <div className="flex items-center gap-2" data-testid="info=>behavior:rule:profile:value">
                                        {PROFILE_DROPDOWN_FIELD_TYPES.includes(profileFieldType ?? '') && (
                                          <CustomTooltip
                                            content={isRowPending(index, criteriaIndex) ? "Loading the answers for this message" : ""}
                                            disabled={!isRowPending(index, criteriaIndex)}
                                          >
                                            <Selectbox
                                              variant="pill"
                                              key={`profile-value-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                              options={profileFieldValueOptions}
                                              value={criteria.value}
                                              disabled={!criteria.question || disabled || isRowPending(index, criteriaIndex)}
                                              onValueChange={(newValue) => {
                                                onChange({
                                                  ...value,
                                                  rules: {
                                                    ...(value as RulesCondition)?.rules,
                                                    criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, value: newValue } : c),
                                                  },
                                                });
                                                setDropdownOpen(index, criteriaIndex, 'response', false);
                                              }}
                                              placeholder="Select value..."
                                              className="w-auto min-w-5 max-w-60"
                                              open={isDropdownOpen(index, criteriaIndex, 'response')}
                                              onOpenChange={(open) => setDropdownOpen(index, criteriaIndex, 'response', open)}
                                            />
                                          </CustomTooltip>
                                        )}
                                        {profileFieldType === 'input' && (
                                          <Input
                                            type="text"
                                            value={criteria.value}
                                            disabled={!criteria.question || disabled}
                                            onChange={(e) => {
                                              const nextValue = e.target.value;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, value: nextValue } : c),
                                                },
                                              });
                                            }}
                                            placeholder="Enter value..."
                                            className={cn(rulePillClasses(), "w-auto min-w-5 max-w-60")}
                                          />
                                        )}
                                        {(profileFieldType === 'date' || profileFieldType === 'time') && (
                                          <Input
                                            type={profileFieldType}
                                            value={criteria.value}
                                            disabled={!criteria.question || disabled}
                                            onChange={(e) => {
                                              const nextValue = e.target.value;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, value: nextValue } : c),
                                                },
                                              });
                                            }}
                                            className={cn(rulePillClasses(), "w-auto")}
                                          />
                                        )}
                                      </div>
                                    )}
                                    {isOnboardingResponse && (
                                      <div className="flex items-center gap-2" data-testid="info=>behavior:rule:onboarding:value">
                                        {PROFILE_DROPDOWN_FIELD_TYPES.includes(profileFieldType ?? '') && (
                                          <CustomTooltip
                                            content={isRowPending(index, criteriaIndex) ? "Loading the answers for this message" : ""}
                                            disabled={!isRowPending(index, criteriaIndex)}
                                          >
                                            <Selectbox
                                              variant="pill"
                                              key={`onboarding-value-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                              options={onboardingResponseValueOptions}
                                              value={criteria.value}
                                              disabled={!criteria.question || disabled || isRowPending(index, criteriaIndex)}
                                              onValueChange={(newValue) => {
                                                onChange({
                                                  ...value,
                                                  rules: {
                                                    ...(value as RulesCondition)?.rules,
                                                    criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, value: newValue } : c),
                                                  },
                                                });
                                                setDropdownOpen(index, criteriaIndex, 'response', false);
                                              }}
                                              placeholder="Select value..."
                                              className="w-auto min-w-5 max-w-60"
                                              open={isDropdownOpen(index, criteriaIndex, 'response')}
                                              onOpenChange={(open) => setDropdownOpen(index, criteriaIndex, 'response', open)}
                                            />
                                          </CustomTooltip>
                                        )}
                                        {profileFieldType === 'input' && (
                                          <Input
                                            type="text"
                                            value={criteria.value}
                                            disabled={!criteria.question || disabled}
                                            onChange={(e) => {
                                              const nextValue = e.target.value;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, value: nextValue } : c),
                                                },
                                              });
                                            }}
                                            placeholder="Enter value..."
                                            className={cn(rulePillClasses(), "w-auto min-w-5 max-w-60")}
                                          />
                                        )}
                                        {(profileFieldType === 'date' || profileFieldType === 'time') && (
                                          <Input
                                            type={profileFieldType}
                                            value={criteria.value}
                                            disabled={!criteria.question || disabled}
                                            onChange={(e) => {
                                              const nextValue = e.target.value;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => i === criteriaIndex ? { ...c, value: nextValue } : c),
                                                },
                                              });
                                            }}
                                            className={cn(rulePillClasses(), "w-auto")}
                                          />
                                        )}
                                      </div>
                                    )}
                                    {isTask && (
                                      <div className="flex" data-testid="info=>behavior:rule:window">
                                        <Selectbox
                                          variant="pill"
                                          key={`window-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                          options={TASK_WINDOW_OPTIONS}
                                          value={(criteria as BehaviorRuleStatementCurrent).taskWindow ?? DEFAULT_TASK_WINDOW}
                                          disabled={!criteria.question || disabled || isRowPending(index, criteriaIndex)}
                                          onValueChange={(newValue) => {
                                            const nextWindow = newValue as 'today' | 'yesterday' | 'latest' | 'last-n';
                                            onChange({
                                              ...value,
                                              rules: {
                                                ...(value as RulesCondition)?.rules,
                                                criteria: criteriaToRender.map((c, i) => {
                                                  if (i === criteriaIndex) {
                                                    // LF360-1810 (task-window extension): switching to
                                                    // 'last-n' seeds an editable N + default match mode
                                                    // (only reseeding N when this ISN'T already the
                                                    // 'last-n' window - see seedTaskWindowSwitch());
                                                    // switching back to a single-instance window resets
                                                    // period to 1 and clears matchMode/threshold.
                                                    const seeded = seedTaskWindowSwitch(c as BehaviorRuleStatementCurrent, nextWindow);
                                                    return {
                                                      ...c,
                                                      taskWindow: nextWindow,
                                                      period: seeded.period,
                                                      matchMode: seeded.matchMode,
                                                      threshold: seeded.threshold,
                                                    }
                                                  }
                                                  return c;
                                                })
                                              }
                                            });

                                          }}
                                          placeholder="Select window..."
                                          className="w-auto min-w-5 max-w-60"
                                        />
                                      </div>
                                    )}
                                    {isLastNWindow && (
                                      <>
                                        <div className="flex items-center gap-2" data-testid="info=>behavior:rule:lastn:n">
                                          <span className="text-sm text-muted-foreground">Last</span>
                                          <Input
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={String(criteria.period ?? DEFAULT_LAST_N_PERIOD)}
                                            disabled={!criteria.question || disabled}
                                            onChange={(e) => {
                                              // N is an integer count of instances; truncate any
                                              // fractional input and clamp to a minimum of 1.
                                              const parsed = Math.trunc(Number(e.target.value));
                                              const nextPeriod = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => {
                                                    if (i === criteriaIndex) {
                                                      return {
                                                        ...c,
                                                        period: nextPeriod,
                                                      }
                                                    }
                                                    return c;
                                                  })
                                                }
                                              });
                                            }}
                                            className={cn(rulePillClasses(), "w-16")}
                                          />
                                          <span className="text-sm text-muted-foreground">instances</span>
                                        </div>
                                        <div className="flex items-center gap-2" data-testid="info=>behavior:rule:lastn:mode">
                                          <span className="text-sm text-muted-foreground">Match</span>
                                          <Selectbox
                                            variant="pill"
                                            key={`match-mode-${selectedNodeProps.id}-${index}-${criteriaIndex}`}
                                            options={MATCH_MODE_OPTIONS}
                                            value={currentMatchMode}
                                            disabled={!criteria.question || disabled || isRowPending(index, criteriaIndex)}
                                            onValueChange={(newMatchMode) => {
                                              const nextMatchMode = newMatchMode as 'all' | 'any' | 'at-least';
                                              onChange({
                                                ...value,
                                                rules: {
                                                  ...(value as RulesCondition)?.rules,
                                                  criteria: criteriaToRender.map((c, i) => {
                                                    if (i === criteriaIndex) {
                                                      return {
                                                        ...c,
                                                        matchMode: nextMatchMode,
                                                        // K only applies to 'at-least'; clear it otherwise
                                                        // so a stale threshold never lingers unseen.
                                                        threshold: nextMatchMode === 'at-least'
                                                          ? ((c as BehaviorRuleStatementCurrent).threshold ?? 1)
                                                          : undefined,
                                                      }
                                                    }
                                                    return c;
                                                  })
                                                }
                                              });

                                            }}
                                            placeholder="Select match mode..."
                                            className="w-auto min-w-5 max-w-60"
                                          />
                                        </div>
                                        {currentMatchMode === 'at-least' && (
                                          <div className="flex items-center gap-2" data-testid="info=>behavior:rule:lastn:k">
                                            <span className="text-sm text-muted-foreground">K</span>
                                            <Input
                                              type="number"
                                              min={1}
                                              step={1}
                                              value={String((criteria as BehaviorRuleStatementCurrent).threshold ?? 1)}
                                              disabled={!criteria.question || disabled}
                                              onChange={(e) => {
                                                // K is an integer count; truncate any fractional
                                                // input and clamp to a minimum of 1.
                                                const parsed = Math.trunc(Number(e.target.value));
                                                const nextThreshold = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
                                                onChange({
                                                  ...value,
                                                  rules: {
                                                    ...(value as RulesCondition)?.rules,
                                                    criteria: criteriaToRender.map((c, i) => {
                                                      if (i === criteriaIndex) {
                                                        return {
                                                          ...c,
                                                          threshold: nextThreshold,
                                                        }
                                                      }
                                                      return c;
                                                    })
                                                  }
                                                });
                                              }}
                                              className={cn(rulePillClasses(), "w-16")}
                                            />
                                            <span className="text-sm text-muted-foreground">of the found instances</span>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  {/* LF360-1933: this control is deliberately always visible - the
                                      design draws every rule's trash at once, which hover-reveal
                                      cannot express. The row keeps its named `group/row` and
                                      styles.ts keeps revealOnHoverClasses' row-scoped branch even
                                      though nothing consumes it right now, so the next row-scoped
                                      affordance inherits a tested contract rather than rediscovering
                                      why the branch-level unnamed group is the wrong target.
                                      The offset centres a 16px box on the rule's 31px first line
                                      ((31 - 16) / 2), which is why it stays put when the rule wraps. */}
                                  {!disabled && (
                                    <div className="absolute right-1 top-[7.5px]">
                                    <CustomTooltip content="Remove rule">
                                      <Button
                                        variant="ghost"
                                        type="button"
                                        className={rowDeleteButtonClasses()}
                                        data-rule-delete=""
                                        data-testid="info=>behavior:rule:remove"
                                        onClick={() => {
                                          // Clean up dropdown open states for this criteria
                                          ['condition', 'response', 'operator'].forEach(type => {
                                            const dropdownKey = getDropdownKey(index, criteriaIndex, type as 'condition' | 'response' | 'operator');
                                            setDropdownOpenState(prev => {
                                              const newMap = new Map(prev);
                                              newMap.delete(dropdownKey);
                                              return newMap;
                                            });
                                          });

                                          const updatedCriteria = criteriaToRender.filter((_, i) => i !== criteriaIndex);
                                          onChange({
                                            ...value,
                                            rules: {
                                              ...(value as RulesCondition)?.rules,
                                              criteria: updatedCriteria,
                                            }
                                          })


                                          // INFO: remove entire branch if no criteria rules left on current one
                                          if (updatedCriteria.length === 0 && index !== 0) {
                                            removeStatementHandler(index);
                                          }
                                        }}
                                      >
                                        <Icon type="delete" size="small"/>
                                      </Button>
                                    </CustomTooltip>
                                    </div>
                                  )}
                                </div>
                                {criteriaIndex === 0 && criteriaToRender.length > 1 && <AndOrToggle disabled={disabled} />}
                                </Fragment>
                              )
                            })}
                            {(value as RulesCondition)?.rules && !disabled && (
                              <AddRow
                                label="Add rule"
                                testId="info=>behavior:rule:add"
                                onClick={() => {
                                  // Update with new criteria
                                  update(index, {
                                    ...value,
                                    rules: {
                                      ...(value as RulesCondition)?.rules,
                                      criteria: [
                                        ...criteriaToRender,
                                        { question: '', value: '', operator: 'eq' as Operator, period: 1 } as BehaviorRuleStatementCurrent
                                      ]
                                    }
                                  });

                                  // LF360-1933 (BRM-03): the rule just appended is the one that
                                  // is settling, so it is the only row that goes pending. Every
                                  // rule already on screen stays interactive - that is the whole
                                  // requirement, and __tests__/e2e/behavior-rule-geometry.spec.mjs
                                  // asserts the first rule's control is still enabled here.
                                }}
                              />
                            )}
                            </div>
                            {(value as ElseCondition)?.type && (
                              <div className={cn(branchHeaderClasses(), { 'opacity-50': disabled })}>Else</div>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
        {/* Render Else node separately outside drag context */}
        {((fields.length > 0 && fields[fields.length - 1]) as ElseCondition)?.type === 'else' && (
          <div className={cn(branchBlockClasses(), { 'opacity-50': disabled })}>
            <div className={branchHeaderClasses()}>Else</div>
          </div>
        )}
        {!disabled && (
          <div className="px-4 pb-4">
            <AddRow
              label="Add branch"
              testId="info=>behavior:branch:add"
              onClick={() => addBranchHandler()}
            />
          </div>
        )}
        </div>
      </Form>
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export const BehaviorRulesNodeInfo = memo(
  BehaviorRulesNodeInfoComponent,
  (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
      prevProps.selectedNodeProps.id === nextProps.selectedNodeProps.id &&
      prevProps.disabled === nextProps.disabled &&
      JSON.stringify(prevProps.selectedNodeProps.data) === JSON.stringify(nextProps.selectedNodeProps.data)
    );
  }
);
